import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Gallery } from "../features/catalog/Gallery";
import { useProduct } from "../features/catalog/useProduct";
import { cartQtyOf, useCart } from "../features/cart/useCart";
import { useSetCartItem } from "../features/cart/useSetCartItem";
import { messages } from "../lib/messages";
import { mainButton } from "../lib/telegram";

const ADDED_LABEL_DURATION_MS = 3000;

/**
 * ProductPage — карточка товара (docs/07-miniapp-ux.md §2.3): галерея, цена, описание,
 * категория, MainButton «Добавить в корзину» → «Перейти в корзину» на 3с.
 * Добавление сразу пишет в серверную корзину (PUT /api/v1/cart/items/{id}).
 */
export function ProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const cart = useCart();
  const setCartItem = useSetCartItem();
  const product = useProduct(productId ?? "");
  const [justAdded, setJustAdded] = useState(false);

  useEffect(() => {
    if (!justAdded) {
      return;
    }
    const timer = setTimeout(() => setJustAdded(false), ADDED_LABEL_DURATION_MS);
    return () => clearTimeout(timer);
  }, [justAdded]);

  useEffect(() => {
    if (!product.data) {
      return;
    }
    const item = product.data;

    if (justAdded) {
      mainButton.setText(messages.goToCart);
      mainButton.show();
      return mainButton.onClick(() => navigate("../cart"));
    }

    mainButton.setText(`${messages.addToCart} · ${item.price_text}`);
    mainButton.show();
    return mainButton.onClick(() => {
      setCartItem(item.id, cartQtyOf(cart.data, item.id) + 1);
      setJustAdded(true);
    });
  }, [product.data, justAdded, cart.data, setCartItem, navigate]);

  useEffect(() => {
    return () => mainButton.hide();
  }, []);

  if (product.isPending) {
    return <ProductSkeleton />;
  }

  if (product.isError) {
    return <p className="p-4 text-center text-hint">{messages.productNotFound}</p>;
  }

  const item = product.data;

  return (
    <div className="min-h-screen bg-bg pb-6">
      <Gallery images={item.images} alt={item.title} />
      <div className="flex flex-col gap-2 p-4">
        <h1 className="text-lg font-semibold text-text">{item.title}</h1>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold text-text">{item.price_text}</span>
          {item.old_price_text ? <span className="text-hint line-through">{item.old_price_text}</span> : null}
        </div>
        {item.category ? <p className="text-sm text-hint">{item.category.title}</p> : null}
        <p className="whitespace-pre-line text-text">{item.description}</p>
      </div>
    </div>
  );
}

function ProductSkeleton() {
  return (
    <div className="min-h-screen bg-bg p-4">
      <div className="aspect-square animate-pulse rounded-card bg-surface" />
    </div>
  );
}
